import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function DevTools() {
  const { trainer } = useAuth();
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [schema, setSchema] = useState(null);
  const [view, setView] = useState('data'); // 'data' | 'schema'
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Gate: dev only
  useEffect(() => {
    if (trainer && trainer.role !== 'dev') navigate('/', { replace: true });
  }, [trainer, navigate]);

  // Load table list
  useEffect(() => {
    api.get('/dev/tables').then(setTables).catch(err => setError(err.message));
  }, []);

  const loadTable = useCallback(async (tableName, p = 1) => {
    setLoading(true);
    setError('');
    try {
      const [data, sch] = await Promise.all([
        api.get(`/dev/tables/${tableName}?page=${p}&limit=50`),
        api.get(`/dev/tables/${tableName}/schema`),
      ]);
      setTableData(data);
      setSchema(sch.schema);
      setPage(p);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  function selectTable(name) {
    setSelected(name);
    setView('data');
    loadTable(name, 1);
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#0D0D0D', color: '#fff', fontFamily: 'monospace' }}>

      {/* Header */}
      <div style={{ background: '#1A1A1A', borderBottom: '1px solid #2E2E2E', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => navigate('/')} style={btnStyle}>← Back</button>
        <span style={{ fontSize: 11, background: '#D72638', color: '#fff', borderRadius: 4, padding: '2px 8px', fontWeight: 700, letterSpacing: 1 }}>DEV</span>
        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Database Inspector</h1>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: '#AAA' }}>Read-only · Sensitive columns masked</span>
      </div>

      <div style={{ display: 'flex', height: 'calc(100vh - 57px)' }}>

        {/* Sidebar — table list */}
        <div style={{ width: 220, background: '#111', borderRight: '1px solid #2E2E2E', overflowY: 'auto', flexShrink: 0 }}>
          <div style={{ padding: '12px 16px', fontSize: 10, fontWeight: 700, color: '#555', letterSpacing: 1 }}>TABLES ({tables.length})</div>
          {tables.map(t => (
            <button key={t.table_name} onClick={() => selectTable(t.table_name)}
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px',
                background: selected === t.table_name ? '#1A1A1A' : 'transparent',
                border: 'none', borderLeft: selected === t.table_name ? '3px solid #D72638' : '3px solid transparent',
                color: selected === t.table_name ? '#fff' : '#AAA',
                cursor: 'pointer', fontSize: 13, fontFamily: 'monospace',
              }}>
              <div>{t.table_name}</div>
              <div style={{ fontSize: 10, color: '#555', marginTop: 1 }}>{t.row_count ?? '?'} rows</div>
            </button>
          ))}
        </div>

        {/* Main panel */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!selected ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#444', fontSize: 14 }}>
              Select a table
            </div>
          ) : (
            <>
              {/* Table toolbar */}
              <div style={{ background: '#1A1A1A', borderBottom: '1px solid #2E2E2E', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 15, fontWeight: 700 }}>{selected}</span>
                {tableData && <span style={{ fontSize: 12, color: '#AAA' }}>{tableData.pagination.total} rows</span>}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                  <button onClick={() => setView('data')}   style={{ ...tabBtn, background: view === 'data'   ? '#D72638' : '#2E2E2E' }}>Data</button>
                  <button onClick={() => setView('schema')} style={{ ...tabBtn, background: view === 'schema' ? '#D72638' : '#2E2E2E' }}>Schema</button>
                  <button onClick={() => loadTable(selected, page)} style={btnStyle}>Refresh</button>
                </div>
              </div>

              {error && <div style={{ padding: 16, color: '#D72638', fontSize: 13 }}>{error}</div>}

              {loading ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#AAA' }}>Loading…</div>
              ) : view === 'schema' ? (
                <SchemaView schema={schema} />
              ) : tableData ? (
                <DataView
                  tableData={tableData}
                  onPage={p => loadTable(selected, p)}
                />
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Data table view ────────────────────────────────────────────────────────
function DataView({ tableData, onPage }) {
  const { columns, rows, pagination } = tableData;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ flex: 1, overflowX: 'auto', overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#1A1A1A', position: 'sticky', top: 0 }}>
              {columns.map(col => (
                <th key={col} style={{ padding: '8px 12px', textAlign: 'left', color: '#AAA', fontWeight: 600, borderBottom: '1px solid #2E2E2E', whiteSpace: 'nowrap' }}>
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #1A1A1A', background: i % 2 === 0 ? 'transparent' : '#0A0A0A' }}>
                {columns.map(col => (
                  <td key={col} style={{ padding: '7px 12px', color: row[col] === null ? '#444' : row[col] === '[hidden]' ? '#555' : '#E0E0E0', whiteSpace: 'nowrap', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', fontStyle: row[col] === null ? 'italic' : 'normal' }}>
                    {row[col] === null ? 'NULL' : String(row[col])}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={columns.length} style={{ padding: 24, textAlign: 'center', color: '#444' }}>No rows</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div style={{ background: '#1A1A1A', borderTop: '1px solid #2E2E2E', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#AAA' }}>
          <button onClick={() => onPage(pagination.page - 1)} disabled={pagination.page <= 1} style={btnStyle}>← Prev</button>
          <span>Page {pagination.page} of {pagination.pages} · {pagination.total} rows</span>
          <button onClick={() => onPage(pagination.page + 1)} disabled={pagination.page >= pagination.pages} style={btnStyle}>Next →</button>
        </div>
      )}
    </div>
  );
}

// ── Schema view ────────────────────────────────────────────────────────────
function SchemaView({ schema }) {
  if (!schema) return null;
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: '#1A1A1A' }}>
            {['Column', 'Type', 'Nullable', 'Default'].map(h => (
              <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: '#AAA', fontWeight: 600, borderBottom: '1px solid #2E2E2E' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {schema.map((col, i) => (
            <tr key={col.column_name} style={{ borderBottom: '1px solid #1A1A1A', background: i % 2 === 0 ? 'transparent' : '#0A0A0A' }}>
              <td style={{ padding: '7px 12px', color: '#fff', fontWeight: 600 }}>{col.column_name}</td>
              <td style={{ padding: '7px 12px', color: '#7EC8E3' }}>{col.data_type}{col.character_maximum_length ? `(${col.character_maximum_length})` : ''}</td>
              <td style={{ padding: '7px 12px', color: col.is_nullable === 'YES' ? '#888' : '#D72638' }}>{col.is_nullable === 'YES' ? 'YES' : 'NO'}</td>
              <td style={{ padding: '7px 12px', color: '#888', fontSize: 11 }}>{col.column_default ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const btnStyle = {
  background: '#2E2E2E', border: 'none', color: '#fff', padding: '6px 12px',
  borderRadius: 6, cursor: 'pointer', fontSize: 12, fontFamily: 'monospace',
};
const tabBtn = {
  border: 'none', color: '#fff', padding: '5px 12px',
  borderRadius: 6, cursor: 'pointer', fontSize: 12, fontFamily: 'monospace',
};
